import { loginSchema } from "@closer/shared";
import { Button, FormField, Input, useToast } from "@closer/ui";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import type { z } from "zod";

import { ShieldIcon } from "../../components/icons";
import { AuthLayout } from "../../layouts/AuthLayout";
import { ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { inviteReturnPath } from "../../lib/inviteReturn";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import { SocialAuthButtons } from "./SocialAuthButtons";

type LoginValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { user, isLoading, login, completeTwoFactor } = useAuth();
  /**
   * ⚠ Meydan okuma token'ı BELLEKTE, adres ya da depoda değil: tek kullanımlık ve kısa
   * ömürlü bir kimlik parçası; adrese koysaydık tarayıcı geçmişinde ve paylaşılan bir
   * bağlantıda kalırdı.
   */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [kurtarmaModu, setKurtarmaModu] = useState(false);
  const [kod, setKod] = useState("");
  const [ikinciHata, setIkinciHata] = useState<string | null>(null);
  const [dogrulaniyor, setDogrulaniyor] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const form = useZodForm(loginSchema, {
    defaultValues: { email: "", password: "" } as LoginValues,
  });

  /**
   * Davetten gelindiyse dönüş davet ekranına (CLOS-74 `[035]`); yoksa uygulamaya.
   * Erken yönlendirme de aynı hedefi kullanıyor: zaten oturumu olan kullanıcı bağlantıya
   * tıkladığında buraya uğrayıp savrulmasın.
   *
   * ⚠ HEDEF `/tenants` DEĞİL `/app` — saha bulgusu (4 Eylül 2026). Dört çalışma alanı
   * olan kullanıcı HER girişte alan seçim ekranıyla karşılaşıyordu. Seçim aslında
   * hatırlanıyor (`localStorage`) ve alan kapısı kayıtlı geçerli bir alan varsa seçim
   * ekranını göstermiyor — ama burası o kapıyı ATLAYIP doğrudan seçime yönlendiriyordu.
   * Yani ürün seçimi hatırlıyor, giriş akışı sormaya devam ediyordu.
   *
   * ⚠ KARAR BURADA DEĞİL KAPIDA: "kayıtlı alana git" kuralını buraya yazmak aynı kuralın
   * iki yerde yaşaması olurdu. Kapı zaten üç durumu ayırıyor — kayıtlı alan geçerli,
   * erişim kaybedilmiş (yedeğe geçiş), hiç alan yok (seçim ekranı). Hiç alanı olmayan
   * yeni kullanıcı da bu yolla seçime gidiyor; yalnız o kararı veren yer tek.
   */
  const hedef = inviteReturnPath(location.search) ?? "/app";

  if (!isLoading && user) return <Navigate to={hedef} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const sonuc = await login(values);
      if (sonuc.durum === "ikinci-adim") {
        setChallenge(sonuc.challenge);
        return;
      }
      navigate(hedef);
    } catch (error) {
      applyServerErrors(form, error);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "Giriş yapılamadı. Lütfen tekrar deneyin.",
      );
    }
  });

  async function ikinciAdim() {
    if (!challenge) return;
    setDogrulaniyor(true);
    setIkinciHata(null);
    try {
      await completeTwoFactor(
        kurtarmaModu ? { challenge, recoveryCode: kod } : { challenge, code: kod },
      );
      navigate(hedef);
    } catch (error) {
      /** Metin sunucudan: "kod yanlış" ile "süre doldu" kullanıcı için farklı şeyler. */
      setIkinciHata(error instanceof ApiRequestError ? error.message : "Doğrulama tamamlanamadı.");
    } finally {
      setDogrulaniyor(false);
    }
  }

  if (challenge) {
    return (
      <AuthLayout
        title="İki Aşamalı Doğrulama"
        description="Hesabınıza girmek için ikinci adımı tamamlayın."
        icon={<ShieldIcon className="size-6" />}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-fg-soft">
            {kurtarmaModu
              ? "Kurtarma kodlarınızdan birini girin."
              : "Doğrulama uygulamanızdaki altı haneli kodu girin."}
          </p>

          <FormField label={kurtarmaModu ? "Kurtarma kodu" : "Doğrulama kodu"} required>
            <Input
              aria-label={kurtarmaModu ? "Kurtarma kodu" : "Doğrulama kodu"}
              value={kod}
              autoComplete="one-time-code"
              onChange={(e) => setKod(e.target.value)}
            />
          </FormField>

          {ikinciHata ? (
            <p role="alert" className="text-sm text-danger">
              {ikinciHata}
            </p>
          ) : null}

          <Button onClick={() => void ikinciAdim()} loading={dogrulaniyor} disabled={kod === ""}>
            Doğrula
          </Button>

          {/* ⚠ Telefonunu kaybeden kullanıcının tek çıkışı bu; gizlemek onu kilitlerdi. */}
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setKurtarmaModu((a) => !a);
              setKod("");
              setIkinciHata(null);
            }}
          >
            {kurtarmaModu ? "Doğrulama kodu kullan" : "Kurtarma kodu kullan"}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Hesabınıza giriş yapın"
      description="Devam etmek için e-posta adresiniz ve şifrenizle giriş yapın."
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <FormField label="E-posta" error={form.formState.errors.email?.message} required>
          <Input type="email" autoComplete="email" {...form.register("email")} />
        </FormField>
        <FormField label="Şifre" error={form.formState.errors.password?.message} required>
          <Input type="password" autoComplete="current-password" {...form.register("password")} />
        </FormField>
        <Button type="submit" loading={form.formState.isSubmitting}>
          Giriş Yap
        </Button>
        <SocialAuthButtons label="Google ile devam et" />
        <p className="text-center text-sm text-fg-soft">
          <Link to="/forgot-password" className="font-medium text-primary hover:underline">
            Şifremi unuttum
          </Link>
        </p>
        <p className="text-center text-sm text-fg-soft">
          Hesabınız yok mu?{" "}
          {/* Davet bilgisi karşı sayfaya da taşınıyor: davetli "kayıt olun"a tıkladığında
                token'ı kaybederse hesabı açar ama davetine dönemezdi. */}
          <Link
            to={`/register${location.search}`}
            className="font-medium text-primary hover:underline"
          >
            Kayıt olun
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
